/**
 * ApiTokensPanel — manage API tokens for programmatic access.
 *
 * Displays the list of existing tokens with revoke functionality.
 * Token generation is handled by ConnectionGuideModal (opened via
 * the "Connect a client" button in the panel header).
 *
 * Used in SettingsPage.
 */

import { useState } from 'react';
import { useApiTokens, useDeleteApiToken } from '../../hooks/useApiTokens';
import { ConnectionGuideModal } from '../ConnectionGuideModal/ConnectionGuideModal';
import type { ApiTokenSummary } from '../../types';

// ============================================================================
// Helpers
// ============================================================================

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ============================================================================
// Token row
// ============================================================================

interface TokenRowProps {
  token: ApiTokenSummary;
  onRevoke: (id: string) => void;
  isRevoking: boolean;
}

function TokenRow({ token, onRevoke, isRevoking }: TokenRowProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  function handleDeleteClick() {
    if (confirmDelete) {
      onRevoke(token.id);
      setConfirmDelete(false);
    } else {
      setConfirmDelete(true);
    }
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-3 border-b border-gray-700 last:border-0">
      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-white truncate">{token.name}</span>
          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-700 text-gray-300 font-mono">
            {token.prefix}...
          </span>
        </div>
        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
          <span className="text-xs text-gray-500">
            Created {formatDate(token.createdAt)}
          </span>
          <span className="text-xs text-gray-500">
            Last used: {formatDate(token.lastUsedAt)}
          </span>
          {token.expiresAt && (
            <span className="text-xs text-amber-500">
              Expires {formatDate(token.expiresAt)}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        {confirmDelete ? (
          <div className="flex items-center gap-1">
            <span className="text-xs text-amber-400">Revoke?</span>
            <button
              type="button"
              onClick={handleDeleteClick}
              disabled={isRevoking}
              className="px-2 py-1 text-xs text-white bg-red-700 hover:bg-red-600 rounded transition-colors disabled:opacity-50"
            >
              {isRevoking ? '...' : 'Yes'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="px-2 py-1 text-xs text-gray-400 hover:text-white bg-gray-700 rounded transition-colors"
            >
              No
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleDeleteClick}
            className="px-2 py-1 text-xs text-gray-400 hover:text-red-400 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded transition-colors"
            aria-label={`Revoke token ${token.name}`}
          >
            Revoke
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Connect button (reusable inside the panel)
// ============================================================================

interface ConnectButtonProps {
  onClick: () => void;
}

function ConnectButton({ onClick }: ConnectButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white bg-primary-600 hover:bg-primary-500 rounded-lg transition-colors shrink-0 font-medium"
    >
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 2L3 14h7l-1 8 10-12h-7z" />
      </svg>
      Connect a client
    </button>
  );
}

// ============================================================================
// Main panel
// ============================================================================

export function ApiTokensPanel() {
  const { data: tokens, isLoading, isError } = useApiTokens();
  const deleteMutation = useDeleteApiToken();
  const [modalOpen, setModalOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
        <p className="text-sm text-gray-400">Loading API tokens...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
        <p className="text-sm text-red-400">Failed to load API tokens.</p>
      </div>
    );
  }

  const list = tokens ?? [];

  return (
    <>
      <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-100 uppercase tracking-wide">
              API Tokens
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Generate tokens for Claude Code MCP and other integrations
            </p>
          </div>
          <ConnectButton onClick={() => setModalOpen(true)} />
        </div>

        {/* Token list or empty state */}
        {list.length === 0 ? (
          <div className="py-4 text-center space-y-3">
            <p className="text-sm text-gray-500">
              No tokens yet — connect a client to create one.
            </p>
            <ConnectButton onClick={() => setModalOpen(true)} />
          </div>
        ) : (
          <div>
            {list.map((token) => (
              <TokenRow
                key={token.id}
                token={token}
                onRevoke={(id) => deleteMutation.mutate(id)}
                isRevoking={deleteMutation.isPending && deleteMutation.variables === token.id}
              />
            ))}
          </div>
        )}

        {list.length > 0 && (
          <p className="text-xs text-gray-500">
            Tokens grant full API access. Revoke any token you no longer use or that may have been
            compromised.
          </p>
        )}
      </div>

      {/* Connection guide modal */}
      <ConnectionGuideModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}
